/**
 * LP Home Sauna JavaScript (assets/lp-home-sauna.js)
 * ================================================================================
 * 自宅サウナ相談LP用のJavaScript
 * 
 * 【機能】
 * 1. スクロールアニメーション（IntersectionObserver）
 * 2. GAS（Google Apps Script）連携フォーム送信
 * 3. 連絡方法（メール/電話）による動的必須切替
 * 4. フォームバリデーション
 * 5. Meta広告トラッキング
 * 
 * 【初心者向け解説】
 * - このファイルはページが読み込まれた後に実行されます
 * - GASとはGoogle Apps Scriptの略で、フォームデータをGoogleスプレッドシートに記録し、
 *   メール通知を送信するためのサービスです
 * ================================================================================
 */

// GAS Web App URL（スプレッドシート連携 + メール送信用）
// ※ sauna-form.js と同じGASデプロイURLを使用
const LP_GAS_WEB_APP_URL = 'https://script.google.com/macros/s/AKfycbxaTJqdAtAVtKbgEJblmxJATP4gSlsV2qd9ZkdtOxAV-yvmXnU6tgMnoKTDN-tEGPEo/exec';

// フォームID（テンプレート側と一致させる）
const LP_FORM_ID = 'LpHomeSaunaForm';

/**
 * LP用GA4イベント送信ヘルパー
 * gtagの読み込み前でもdataLayerへ積めるようにして、計測欠損を減らす。
 */
function lpSendGAEvent(eventName, params) {
    var payload = Object.assign({
        page_path: window.location.pathname,
        page_location: window.location.href,
        transport_type: 'beacon'
    }, params || {});

    try {
        if (typeof window.gtag === 'function') {
            window.gtag('event', eventName, payload);
        } else {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(Object.assign({ event: eventName }, payload));
        }
    } catch (e) {
        // GA4送信失敗はユーザー体験に影響させない
    }
}

function lpGetSectionMeta(el) {
    var section = el && el.closest ? el.closest('.lp-home-sauna [data-ga-section-id]') : null;
    if (!section) {
        return {
            section_id: '',
            section_name: '',
            section_index: 0
        };
    }

    return {
        section_id: section.getAttribute('data-ga-section-id') || '',
        section_name: section.getAttribute('data-ga-section-name') || '',
        section_index: parseInt(section.getAttribute('data-ga-section-index'), 10) || 0
    };
}

function lpGetCTALocation(link) {
    if (!link) return 'unknown';
    if (link.closest('.sp-fixed-buttons')) return 'sticky_mobile';

    var meta = lpGetSectionMeta(link);
    if (meta.section_name) return meta.section_name;

    return 'unknown';
}

function lpThrottle(fn, wait) {
    var timer = null;
    var lastRun = 0;

    return function() {
        var now = Date.now();
        var args = arguments;
        var context = this;

        if (now - lastRun >= wait) {
            lastRun = now;
            fn.apply(context, args);
            return;
        }

        if (timer) return;
        timer = setTimeout(function() {
            timer = null;
            lastRun = Date.now();
            fn.apply(context, args);
        }, wait - (now - lastRun));
    };
}

// ================================================================================
// ⓪ ボット対策（パッシブ型 - ユーザーへの影響ゼロ）
// ================================================================================
// 以下の3つの方法を組み合わせて、reCAPTCHA等を使わずにボットを検出します。
//
// 1. ハニーポット: 隠しフィールドに値があればボット
// 2. 時間ベース: ページ表示から送信まで3秒未満ならボット
// 3. 操作検知: フォーム内でキー入力・マウス移動・タッチが一度もなければボット
//
// これらは外部サービスに依存しないため、以下の環境でも問題なく動作します：
// - シークレットモード / プライベートブラウジング
// - 厳しいセキュリティソフト / アドブロッカー
// - Cookie無効環境
// - ShopifyのhCaptchaとの競合もなし
// ================================================================================

/**
 * ボット対策の状態管理オブジェクト
 * ページ読み込み時にタイムスタンプを記録し、ユーザー操作を追跡します
 */
var lpBotProtection = {
    // ページ読み込み時刻（ミリ秒）
    pageLoadTime: Date.now(),

    // ユーザーがフォーム内で何らかの操作をしたかどうか
    // （キー入力、マウス移動、タッチ操作のいずれか）
    userInteracted: false,

    // 最小送信時間（ミリ秒）：ページ表示からこの時間が経過しないと送信できない
    // 人間がフォームに入力するには最低でも数秒はかかるため、3秒に設定
    MIN_SUBMIT_TIME_MS: 3000
};

/**
 * ボット対策の初期化
 * フォーム内のユーザー操作を監視して、人間かどうかを判定するフラグを立てます
 */
function initBotProtection() {
    var form = document.getElementById(LP_FORM_ID);
    if (!form) return;

    // ユーザー操作を検知するイベントリスナーを登録
    // どれか1つでも発火すれば「人間が操作している」と判定

    // キーボード入力（フォーム内でキーを押した場合）
    form.addEventListener('keydown', function() {
        lpBotProtection.userInteracted = true;
    }, { once: true }); // once: true で1回だけ実行（パフォーマンス対策）

    // マウス移動（フォーム内でマウスを動かした場合 = PCユーザー）
    form.addEventListener('mousemove', function() {
        lpBotProtection.userInteracted = true;
    }, { once: true });

    // タッチ操作（フォーム内をタップした場合 = スマホ/タブレットユーザー）
    form.addEventListener('touchstart', function() {
        lpBotProtection.userInteracted = true;
    }, { once: true });

    // クリック操作（フォーム内でクリックした場合）
    form.addEventListener('click', function() {
        lpBotProtection.userInteracted = true;
    }, { once: true });

}

/**
 * ボット判定チェック
 * フォーム送信時に呼び出し、ボットかどうかを判定します
 * 
 * @returns {Object} { isBot: boolean, reasons: string[] }
 *   - isBot: true ならボットと判定、送信をブロック
 *   - reasons: ブロック理由の配列（ログ・デバッグ用）
 */
function lpCheckForBot() {
    var reasons = [];

    // --- チェック1: ハニーポット ---
    // 隠しフィールド（website_url）に値が入っていたらボット
    var honeypot = document.getElementById('lp-website-url');
    if (honeypot && honeypot.value.trim() !== '') {
        reasons.push('HONEYPOT_FILLED');
    }

    // --- チェック2: 時間ベース ---
    // ページ表示から送信までの経過時間が短すぎたらボット
    var elapsed = Date.now() - lpBotProtection.pageLoadTime;
    if (elapsed < lpBotProtection.MIN_SUBMIT_TIME_MS) {
        reasons.push('TOO_FAST_SUBMIT');
    }

    // --- チェック3: ユーザー操作検知 ---
    // フォーム内で一度もキー入力・マウス移動・タッチがなかったらボット
    if (!lpBotProtection.userInteracted) {
        reasons.push('NO_USER_INTERACTION');
    }

    return {
        isBot: reasons.length > 0,
        reasons: reasons
    };
}

// ================================================================================
// ① スクロールアニメーション（IntersectionObserver）
// ================================================================================

document.addEventListener('DOMContentLoaded', function() {
    // --- LP専用スムーススクロール ---
    // common-fixed.js のスムーススクロールはヘッダー高さを参照するが、
    // LP専用レイアウトにはヘッダーが存在しないため、
    // PC（画面幅 > 767px）で $(\"header\").innerHeight() が undefined を返し、
    // scrollTop が NaN になってページ先頭にジャンプしてしまう。
    // この処理で LP 内のアンカーリンクを正しくスクロールさせる。
    initLPSmoothScroll();

    // --- スクロールアニメーション ---
    initScrollReveal();

    // --- GA4 セクション表示イベント計測 ---
    initSectionViewTracking();

    // --- GA4 CTAクリックイベント計測 ---
    initLPCTATracking();

    // --- フォーム関連の初期化 ---
    var form = document.getElementById(LP_FORM_ID);
    if (form) {
        initBotProtection();          // ボット対策（ハニーポット・時間・操作検知）
        initContactMethodControl();
        initFormValidation();
        initGASSubmission();
        initNumberFieldsNormalization();
    }
});

/**
 * LP専用スムーススクロールの初期化
 * common-fixed.js のハンドラーより先にイベントを処理し、
 * ヘッダーオフセットなしで正しくスクロールさせる
 */
function initLPSmoothScroll() {
    // .lp-home-sauna 内のアンカーリンクのみを対象にする
    var anchorLinks = document.querySelectorAll('.lp-home-sauna a[href^="#"]');
    
    anchorLinks.forEach(function(link) {
        link.addEventListener('click', function(e) {
            var href = this.getAttribute('href');
            if (!href || href === '#') return;

            var target = document.querySelector(href);
            if (target) {
                // デフォルト動作とイベント伝播を停止
                // （common-fixed.js のハンドラーに処理させない）
                e.preventDefault();
                e.stopPropagation();

                // ヘッダーなしでスムーススクロール
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

            }
        }, true); // キャプチャフェーズで実行（common-fixed.js より先に処理）
    });

}

/**
 * スクロールアニメーションの初期化
 * 画面に要素が入ったタイミングでフェードインアニメーションを実行します
 */
function initScrollReveal() {
    var observerOptions = {
        root: null,
        rootMargin: '0px 0px -30px 0px',
        threshold: 0.1
    };

    var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);

    // .hero セクションは最初から表示（is-visible を付与済み）
    document.querySelectorAll('.lp-home-sauna .js-reveal:not(.hero)').forEach(function(el) {
        observer.observe(el);
    });
}

/**
 * GA4 セクション表示イベント計測
 * 各セクション（data-ga-section-id 属性を持つ要素）が画面上部〜中段へ入った
 * タイミングで、1ページロードにつき1回だけ section_view イベントを送信する。
 *
 * 送信先: GA4 (G-K0EWXKRTEC) ※テンプレート側で send_page_view:false で初期化済み
 * 送信パラメータ: section_id / section_name / section_index / page_path など
 * 重複排除: 一度発火したセクションは unobserve して再送しない
 */
function initSectionViewTracking() {
    var sections = Array.prototype.slice.call(document.querySelectorAll('.lp-home-sauna [data-ga-section-id]'));
    if (sections.length === 0) return;

    var fired = {};

    function fireSectionView(el, triggerType) {
        var meta = lpGetSectionMeta(el);
        if (!meta.section_id || fired[meta.section_id]) return;

        fired[meta.section_id] = true;

        var rect = el.getBoundingClientRect();
        lpSendGAEvent('section_view', {
            section_id: meta.section_id,
            section_name: meta.section_name,
            section_index: meta.section_index,
            trigger_type: triggerType || 'section_entry',
            section_height: Math.round(rect.height),
            section_top: Math.round(rect.top + window.scrollY),
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight
        });
    }

    function checkVisibleSections() {
        var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        if (!viewportHeight) return;

        sections.forEach(function(el) {
            var meta = lpGetSectionMeta(el);
            if (!meta.section_id || fired[meta.section_id]) return;

            var rect = el.getBoundingClientRect();
            var hasEnteredMainView = rect.top <= viewportHeight * 0.65 && rect.bottom >= viewportHeight * 0.2;
            if (hasEnteredMainView) {
                fireSectionView(el, 'viewport_check');
            }
        });
    }

    if (!('IntersectionObserver' in window)) {
        checkVisibleSections();
        window.addEventListener('scroll', lpThrottle(checkVisibleSections, 250), { passive: true });
        window.addEventListener('resize', lpThrottle(checkVisibleSections, 250));
        return;
    }

    var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
            if (!entry.isIntersecting) return;

            fireSectionView(entry.target, 'section_entry');
            observer.unobserve(entry.target);
        });
    }, {
        root: null,
        rootMargin: '0px 0px -45% 0px',
        threshold: 0
    });

    sections.forEach(function(el) {
        observer.observe(el);
    });

    checkVisibleSections();
}

/**
 * CTAクリック計測
 * どの位置の相談CTAが押されたかを見分ける。
 */
function initLPCTATracking() {
    var links = document.querySelectorAll('.lp-home-sauna a[href="#form-section"]');
    if (!links.length) return;

    links.forEach(function(link) {
        link.addEventListener('click', function() {
            var meta = lpGetSectionMeta(link);
            lpSendGAEvent('lp_cta_click', {
                cta_text: (link.textContent || '').trim(),
                cta_href: link.getAttribute('href') || '',
                cta_location: lpGetCTALocation(link),
                section_id: meta.section_id,
                section_name: meta.section_name,
                section_index: meta.section_index,
                viewport_width: window.innerWidth,
                viewport_height: window.innerHeight
            });
        });
    });
}

// ================================================================================
// ② GAS送信機能
// ================================================================================

/**
 * フォーム送信機能の初期化
 * Shopifyネイティブのcontactフォーム送信を利用し、
 * 送信前にGASバックアップ・トラッキングをfire-and-forgetで実行する。
 * 送信後はページリロード → ?contact_posted=true を検知して完了メッセージを表示。
 */
function initGASSubmission() {
    var form = document.getElementById(LP_FORM_ID);
    if (!form) return;

    // --- リロード後の完了メッセージ表示 ---
    // Shopifyは送信成功後に ?contact_posted=true へリダイレクトする
    if (window.location.search.indexOf('contact_posted=true') !== -1) {
        // OpenAI広告（ChatGPT広告）コンバージョン計測 lead_created
        // ※Shopifyが実際に送信を受理した後のリロード時のみ通る。ボットへのフェイク成功では発火しない。
        try {
            if (window.oaiq) {
                window.oaiq('measure', 'custom', { type: 'custom' }, { custom_event_name: 'idetox_contact_lead' });
            }
        } catch (err) {
            // OpenAI Ads conversion failures must not block success message.
        }
        handleLPSubmissionSuccess();
        return; // フォーム送信ハンドラは登録しない（フォームは非表示になる）
    }

    // --- フォーム送信ハンドラ ---
    form.addEventListener('submit', function(e) {
        // バリデーションチェック（失敗時はネイティブ送信を止める）
        if (!window.lpValidateForm || !window.lpValidateForm()) {
            e.preventDefault();
            return false;
        }

        // ボット対策チェック（ボットにはフェイク成功を見せる）
        var botCheck = lpCheckForBot();
        if (botCheck.isBot) {
            e.preventDefault();
            handleLPSubmissionSuccess();
            return false;
        }
        // --- 送信前にGASバックアップ・トラッキングをfire-and-forget ---
        var inquiryId = lpGenerateInquiryId();
        lpAppendInquiryIdToMessage(inquiryId);

        // GASバックアップ送信
        var gasData = {};
        try {
            gasData = buildLPGASBackupData(inquiryId);
            navigator.sendBeacon(LP_GAS_WEB_APP_URL, JSON.stringify(gasData));
        } catch (err) {
            // GASバックアップ送信失敗（無視）
        }

        if (window.idetoxAdsUserData) {
            var adsEmail = document.getElementById(LP_FORM_ID + '-email');
            var adsPhone = document.getElementById(LP_FORM_ID + '-phone');
            window.idetoxAdsUserData({ email: adsEmail && adsEmail.value, phone: adsPhone && adsPhone.value });
        }

        // GA4 form_submit イベント送信
        lpSendGAEvent('form_submit', {
            event_category: 'contact',
            event_label: 'lp_home_sauna_form',
            form_name: 'lp_home_sauna_form',
            form_id: 'LPHomeSaunaForm',
            inquiry_type: gasData.inquiry_type || 'LP自宅サウナ相談',
            inquiry_id: inquiryId,
            contact_method: gasData.contact_method || '',
            source: 'lp-home-sauna'
        });

        // Meta広告コンバージョン計測
        try {
            if (window.fbq && !sessionStorage.getItem('lp_lead_fired')) {
                fbq('track', 'Lead');
                sessionStorage.setItem('lp_lead_fired', '1');
            }
        } catch (err) {
            // Meta広告送信失敗（無視）
        }

        // Google広告コンバージョン計測
        try {
            if (typeof gtag === 'function') {
                gtag('event', 'conversion', {
                    'send_to': 'AW-10981582719/lp_home_sauna'
                });
            }
        } catch (err) {
            // Google広告送信失敗（無視）
        }

        try {
            if (window.idetoxTrackYahooConversion) {
                window.idetoxTrackYahooConversion({ key: 'lp-home-sauna-' + inquiryId });
            }
        } catch (err) {
            // Yahoo Ads conversion failures must not block form submission.
        }

        lpSetSubmitButtonLoading(true);
    });
}

/**
 * GASバックアップ用のデータを構築
 * contactテンプレートと同じ contact_full 形式で送信
 */
function buildLPGASBackupData(inquiryId) {
    var contactMethod = lpGetRadioValue('contact[ご希望の連絡方法]') || '未選択';
    var device = lpGetDeviceInfo();

    var inquiryTypeSelect = document.getElementById(LP_FORM_ID + '-inquiry-type');
    var inquiryType = inquiryTypeSelect ? inquiryTypeSelect.value : '未選択';

    var nameField = document.getElementById(LP_FORM_ID + '-name');
    var name = nameField ? nameField.value : '';

    var emailField = document.getElementById(LP_FORM_ID + '-email');
    var email = emailField ? emailField.value : '';

    var phoneField = document.getElementById(LP_FORM_ID + '-phone');
    var phone = phoneField ? phoneField.value : '';

    var messageField = document.getElementById(LP_FORM_ID + '-message');
    var message = messageField ? messageField.value : '';

    return {
        action: 'lp_sauna_contact',
        inquiry_id: inquiryId,
        timestamp: new Date().toISOString(),
        name: name,
        email: email,
        phone: phone,
        contact_method: contactMethod,
        inquiry_type: inquiryType,
        message: message,
        source: 'lp-home-sauna',
        device_type: device.type,
        screen_width: device.screenWidth,
        browser: device.browser,
        os: device.os
    };
}

/**
 * 送信成功時の処理
 * フォームを非表示にし、完了メッセージを表示してスクロール
 */
function handleLPSubmissionSuccess() {
    // フォームを非表示にする
    var form = document.getElementById(LP_FORM_ID);
    if (form) {
        form.style.display = 'none';
    }

    // 完了メッセージを表示する
    var successMessage = document.getElementById('lp-form-success');
    if (successMessage) {
        successMessage.classList.add('is-visible');

        // リロード後はページ先頭に戻るので、完了メッセージ位置までスクロール
        setTimeout(function() {
            successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
    }
}

// ================================================================================
// ③ 連絡方法による動的フィールド制御
// ================================================================================

/**
 * 連絡方法選択による動的フィールド表示制御
 * メール/電話の選択に応じて必須項目を切り替えます
 */
function initContactMethodControl() {
    var contactMethodRadios = document.querySelectorAll('#' + LP_FORM_ID + ' input[name="contact[ご希望の連絡方法]"]');
    var emailField = document.getElementById(LP_FORM_ID + '-email');
    var phoneField = document.getElementById(LP_FORM_ID + '-phone');
    var emailGroup = document.getElementById('lp-email-group');
    var phoneGroup = document.getElementById('lp-phone-group');
    var emailReqMarker = document.getElementById('lp-email-req');
    var phoneReqMarker = document.getElementById('lp-phone-req');

    if (!emailField || !phoneField) {
        return;
    }

    function updateContactFields() {
        var selected = document.querySelector('#' + LP_FORM_ID + ' input[name="contact[ご希望の連絡方法]"]:checked');
        if (!selected) return;

        var method = selected.value;

        if (method === 'メール') {
            // メール選択時：メール欄は必須＆表示、電話は任意＆表示
            emailField.setAttribute('required', 'required');
            emailField.setAttribute('data-form_required', 'メールアドレス');
            phoneField.removeAttribute('required');
            phoneField.removeAttribute('data-form_required');

            if (emailGroup) emailGroup.style.display = 'block';
            if (phoneGroup) phoneGroup.style.display = 'block';
            if (emailReqMarker) emailReqMarker.innerHTML = '<span class="req">必須</span>';
            if (phoneReqMarker) phoneReqMarker.innerHTML = '<span class="opt">任意</span>';
        } else if (method === '電話') {
            // 電話選択時：電話欄は必須、メール欄も必須（返信用）
            phoneField.setAttribute('required', 'required');
            phoneField.setAttribute('data-form_required', '電話番号');
            emailField.setAttribute('required', 'required');
            emailField.setAttribute('data-form_required', 'メールアドレス');

            if (emailGroup) emailGroup.style.display = 'block';
            if (phoneGroup) phoneGroup.style.display = 'block';
            if (emailReqMarker) emailReqMarker.innerHTML = '<span class="req">必須</span>';
            if (phoneReqMarker) phoneReqMarker.innerHTML = '<span class="req">必須</span>';
        }
    }

    // 各ラジオボタンに変更イベントを追加
    contactMethodRadios.forEach(function(radio) {
        radio.addEventListener('change', updateContactFields);
    });

    // 初期状態の設定
    updateContactFields();
}

// ================================================================================
// ④ フォームバリデーション
// ================================================================================

function initFormValidation() {
    var form = document.getElementById(LP_FORM_ID);
    if (!form) return;

    /**
     * フォーム全体のバリデーション
     */
    function validateForm() {
        var isValid = true;
        var errors = [];

        // 必須項目をチェック
        var requiredFields = form.querySelectorAll('[data-form_required]');
        requiredFields.forEach(function(field) {
            var fieldName = field.getAttribute('data-form_required');
            var value = field.value.trim();

            if (!value) {
                markFieldAsError(field, fieldName + 'は必須項目です');
                errors.push(fieldName);
                isValid = false;
            } else {
                clearFieldError(field);

                // 詳細バリデーション
                if (field.type === 'email') {
                    var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailPattern.test(value)) {
                        markFieldAsError(field, 'メールアドレスの形式が正しくありません');
                        errors.push('メールアドレス形式');
                        isValid = false;
                    }
                }
                if (field.type === 'tel' && value) {
                    var phonePattern = /^[0-9\-\+\(\)\s]+$/;
                    if (!phonePattern.test(value)) {
                        markFieldAsError(field, '電話番号は半角数字とハイフンで入力してください');
                        errors.push('電話番号形式');
                        isValid = false;
                    }
                }
            }
        });

        // ご相談の内容のチェック
        var inquiryType = document.getElementById(LP_FORM_ID + '-inquiry-type');
        if (inquiryType && !inquiryType.value) {
            markFieldAsError(inquiryType, 'ご相談の内容は必須項目です');
            errors.push('ご相談の内容');
            isValid = false;
        }

        // 連絡方法の選択チェック
        var contactMethod = document.querySelector('#' + LP_FORM_ID + ' input[name="contact[ご希望の連絡方法]"]:checked');
        if (!contactMethod) {
            errors.push('ご希望の連絡方法');
            isValid = false;
        }

        // エラーがある場合
        if (!isValid) {
            var firstErrorField = form.querySelector('.error');
            if (firstErrorField) {
                firstErrorField.focus();
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            alert('必須項目に未入力があります。\n' + errors.join('、') + '\nを入力してください。');
        }

        return isValid;
    }

    // グローバルスコープに公開
    window.lpValidateForm = validateForm;

    /**
     * フィールドをエラー状態にする
     */
    function markFieldAsError(field, message) {
        field.classList.add('error');

        var existingError = field.parentNode.querySelector('.field-error');
        if (existingError) existingError.remove();

        var errorElement = document.createElement('span');
        errorElement.className = 'field-error';
        errorElement.textContent = message;
        field.parentNode.appendChild(errorElement);
    }

    /**
     * フィールドのエラー状態を解除する
     */
    function clearFieldError(field) {
        field.classList.remove('error');
        var errorElement = field.parentNode.querySelector('.field-error');
        if (errorElement) errorElement.remove();
    }

    // リアルタイムバリデーション
    var allInputs = form.querySelectorAll('input, select, textarea');
    allInputs.forEach(function(input) {
        input.addEventListener('blur', function() {
            if (this.hasAttribute('data-form_required') || this.hasAttribute('required')) {
                var fieldName = this.getAttribute('data-form_required') || 'この項目';
                if (!this.value.trim()) {
                    markFieldAsError(this, fieldName + 'は必須項目です');
                } else {
                    clearFieldError(this);
                }
            }
        });

        input.addEventListener('input', function() {
            if (this.classList.contains('error')) {
                clearFieldError(this);
            }
        });
    });
}

// ================================================================================
// ⑤ ユーティリティ関数
// ================================================================================

/**
 * 数字入力欄の全角半角変換機能
 */
function initNumberFieldsNormalization() {
    var telFields = document.querySelectorAll('#' + LP_FORM_ID + ' input[type="tel"]');

    telFields.forEach(function(field) {
        field.addEventListener('input', function(e) {
            var value = e.target.value;
            // 全角数字を半角に変換
            value = value.replace(/[０-９]/g, function(s) {
                return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
            });
            // 全角ハイフンを半角に変換
            value = value.replace(/[－ー−]/g, '-');
            e.target.value = value;
        });
    });
}

/**
 * ラジオボタンの選択値を取得
 */
function lpGetRadioValue(name) {
    var radio = document.querySelector('#' + LP_FORM_ID + ' input[name="' + name + '"]:checked');
    return radio ? radio.value : null;
}

/**
 * 送信ボタンのローディング状態制御
 */
function lpSetSubmitButtonLoading(isLoading) {
    var submitButton = document.getElementById('lp-form-submit');
    if (!submitButton) return;

    if (isLoading) {
        submitButton.disabled = true;
        submitButton.textContent = '送信中...';
        submitButton.style.opacity = '0.7';
        submitButton.style.cursor = 'not-allowed';
    } else {
        submitButton.disabled = false;
        submitButton.textContent = '送信';
        submitButton.style.opacity = '1';
        submitButton.style.cursor = 'pointer';
    }
}

/**
 * 一意の inquiry_id を生成
 * フォーマット: LP-YYYYMMDD-xxxxx
 */
function lpGenerateInquiryId() {
    var now = new Date();
    var dateStr = now.getFullYear().toString() +
                  ('0' + (now.getMonth() + 1)).slice(-2) +
                  ('0' + now.getDate()).slice(-2);
    var chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    var randomStr = '';
    for (var i = 0; i < 5; i++) {
        randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return 'LP-' + dateStr + '-' + randomStr;
}

function lpSetInquiryIdField(inquiryId) {
    var form = document.getElementById(LP_FORM_ID);
    if (!form) return;

    var field = form.querySelector('input[name="contact[inquiry_id]"]');
    if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'contact[inquiry_id]';
        form.appendChild(field);
    }
    field.value = inquiryId;
}

function lpAppendInquiryIdToMessage(inquiryId) {
    var messageField = document.getElementById(LP_FORM_ID + '-message');
    var note = 'inquiry_id: ' + inquiryId;

    lpSetInquiryIdField(inquiryId);

    if (!messageField || messageField.value.indexOf(note) !== -1) return;

    var currentValue = messageField.value.replace(/\s+$/g, '');
    messageField.value = currentValue ? currentValue + '\n\n' + note : note;
}

/**
 * デバイス情報を取得
 */
function lpGetDeviceInfo() {
    var ua = navigator.userAgent;
    var deviceType = 'desktop';
    if (/iPhone|iPod/.test(ua)) deviceType = 'mobile_ios';
    else if (/Android.*Mobile/.test(ua)) deviceType = 'mobile_android';
    else if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) deviceType = 'tablet_ios';
    else if (/Android/.test(ua)) deviceType = 'tablet_android';

    var browser = 'unknown';
    if (/CriOS/.test(ua)) browser = 'Chrome iOS';
    else if (/FxiOS/.test(ua)) browser = 'Firefox iOS';
    else if (/EdgiOS/.test(ua) || /Edg\//.test(ua)) browser = 'Edge';
    else if (/Chrome\//.test(ua) && !/Edge/.test(ua)) browser = 'Chrome';
    else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';

    var os = 'unknown';
    if (/Windows NT 10/.test(ua)) os = 'Windows 10/11';
    else if (/Windows/.test(ua)) os = 'Windows';
    else if (/Mac OS X/.test(ua)) os = 'macOS';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/Linux/.test(ua)) os = 'Linux';

    return { type: deviceType, screenWidth: window.innerWidth, browser: browser, os: os };
}

/**
 * GAS Web App にエラーログを送信
 */
function lpSendLogToGAS(inquiryId, status, errorCodes, errorDetails, extraData) {
    if (!LP_GAS_WEB_APP_URL) return;

    var device = lpGetDeviceInfo();
    var payload = {
        inquiry_id: inquiryId,
        timestamp: new Date().toISOString(),
        status: status,
        error_codes: (errorCodes || []).join(','),
        error_details: (errorDetails || []).join(' | '),
        device_type: device.type,
        screen_width: device.screenWidth,
        browser: device.browser,
        os: device.os,
        contact_method: (extraData && extraData.contactMethod) || '',
        inquiry_type: (extraData && extraData.inquiryType) || 'LP自宅サウナ相談',
        message_preview: ''
    };

    try {
        fetch(LP_GAS_WEB_APP_URL, {
            method: 'POST',
            mode: 'no-cors',
            body: JSON.stringify(payload)
        }).catch(function() {
            // GASエラーログ送信失敗（無視）
        });
    } catch (e) {
        // GASエラーログ送信例外（無視）
    }
}
